/**
 * sceneOffset.ts
 *
 * The "scene position offset" of a pipeline-processed (upload-based) scene.
 *
 * When the pipeline centers an uploaded model it translates all geometry by
 * `report[].translationVector` (T) and records that it did so in
 * `pipelineOptions.centerFeature.isCentered`. Camera positions (viewpoints /
 * BCF issues), however, are persisted in the ORIGINAL, un-centered coordinate
 * frame: every writer subtracts T before saving and every reader adds it back.
 * That contract is shared with the Unity viewer, the Unreal Pixel-Streaming
 * Navigator (see PStreamingUI.tsx) and the historical A-Frame editor
 * (_archive_aframe/features/controller/bcf-system.js), so it cannot be
 * changed unilaterally — only honoured.
 *
 * Scenes built with the floorplanner are never centered, so T is {0,0,0}
 * there and all of this is a no-op.
 */

import { useSceneDocStore } from "contexts/Scenes/useSceneDocStore";

export interface SceneOffset {
	x: number;
	y: number;
	z: number;
}

export const ZERO_SCENE_OFFSET: SceneOffset = { x: 0, y: 0, z: 0 };

/**
 * The translation the pipeline applied when centering `sceneDoc`, in R3F world
 * axes ({0,0,0} when the scene was not centered / has no report).
 */
/**
 * Whether the scene's architecture is stored in the centered (`+T`) frame.
 *
 * This decides whether the offset applies at all, and it is not cosmetic. The
 * old A-Frame renderer applied T to architecture positions *at load time*, but
 * only for children that were not yet marked `isOffsetted` — see
 * _archive_aframe/VRModel.jsx:355. The R3F renderer never ported that step; it
 * places every child at its stored `position` verbatim (sceneParser.ts). So:
 *
 *  - `isOffsetted` truthy: the old editor skipped its migration and drew the
 *    architecture at `position`, exactly where R3F draws it. Both editors share
 *    one world frame, and a viewpoint stored as `camera - T` needs `+ T` back.
 *  - `isOffsetted` absent/false: the old editor drew the architecture at
 *    `position + T` while R3F draws it at `position`. R3F's world is therefore
 *    already shifted by `-T` relative to the frame those viewpoints were
 *    captured in — which is precisely the frame they were stored in. Adding T
 *    again would push the camera a whole translation vector off, typically
 *    below the building. The offset must be zero here.
 *
 * Scenes with no architecture children have nothing that could have been
 * migrated, so they fall in the first case.
 */
const architectureIsOffsetted = (sceneDoc: any): boolean => {
	const children = sceneDoc?.scene?.children;
	if (!Array.isArray(children)) return true;

	// `!objectId` is VRModel's own test for "architecture rather than a library
	// object" — library objects were never translated.
	const architecture = children.filter(
		(child: any) => child?.immType === "ImmModel" && !child?.params?.objectId,
	);
	if (architecture.length === 0) return true;

	return architecture.some(
		(child: any) =>
			child?.params?.isOffsetted === true || child?.params?.isOffsetted === "true",
	);
};

export const getScenePositionOffset = (sceneDoc: any): SceneOffset => {
	if (!sceneDoc) return ZERO_SCENE_OFFSET;

	const isCentered =
		sceneDoc.pipelineOptions?.centerFeature?.isCentered === true ||
		sceneDoc.pipelineOptions?.centerFeature?.isCentered === "true";

	if (isCentered && !architectureIsOffsetted(sceneDoc)) return ZERO_SCENE_OFFSET;

	if (isCentered && sceneDoc.report?.length > 0) {
		for (let index = 0; index < sceneDoc.report.length; index += 1) {
			const element = sceneDoc.report[index];
			if (element?.translationVector?.x !== undefined) {
				return {
					x: element.translationVector.x,
					y: element.translationVector.y,
					z: element.translationVector.z,
				};
			}
		}
	}

	return ZERO_SCENE_OFFSET;
};

/**
 * Same, for callers that don't hold the scene document themselves — the doc
 * store is the single source of truth for the currently open scene.
 */
export const getCurrentSceneOffset = (): SceneOffset =>
	getScenePositionOffset(useSceneDocStore.getState().doc);
